import { createApi } from '@reduxjs/toolkit/query/react';

import { SCOPE_ASSESSOR_API_URL } from 'api/APIBase';
import { handleError, handleRTKQueryError } from 'services/Error';
import { getBaseQuery } from 'store/api/apiBaseQuery';

const TagTypes = {
    MEMBER_DETAILS: 'MEMBER_DETAILS',
};

export interface MemberDetails {
    memberId: string;
    firstName: string;
    lastName: string;
    gender: string;
    dateOfBirth: string;
    benefitGroup?: string;
    benefitGroupName?: string;
    carrier?: string;
    account?: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    zip?: string;
    phone?: string;
    secondaryPhone?: string;
    email?: string;
    enrollmentStatus?: string;
    enrollmentStart?: string;
    enrollmentEnd?: string;
    isFmhEnrollmentEnd?: boolean;
    pendingAt?: string;
    willBeUnmanagedOn?: string;
    lastUpdated?: string;
}

export interface UpdateMemberDataPayload {
    payerId: string;
    memberId: string;
    firstName: string;
    dateOfBirth: string;
    phone: string;
}

export const scopeAssessorApi = createApi({
    baseQuery: getBaseQuery(SCOPE_ASSESSOR_API_URL),
    reducerPath: 'scopeAssessorApi',
    tagTypes: Object.values(TagTypes),
    endpoints: (build) => ({
        getMemberDetails: build.query<
            MemberDetails | undefined,
            {
                payerId: string;
                segmentId: string;
                memberId: string;
                firstName: string;
                lastName: string;
                dateOfBirth: string;
                raiseNotFound?: boolean;
            }
        >({
            query: ({ payerId, segmentId, memberId, firstName, lastName, dateOfBirth }) => ({
                url: `${payerId}/members/${memberId}`,
                params: {
                    segment_id: segmentId,
                    firstname: firstName,
                    lastname: lastName,
                    dateofbirth: dateOfBirth,
                    ignore_dates: true,
                },
            }),
            providesTags: (_results, _error, { memberId }) => [
                { type: TagTypes.MEMBER_DETAILS, id: memberId },
            ],
            transformResponse: (
                response: {
                    member_id: string;
                    firstname: string;
                    lastname: string;
                    dateofbirth: string;
                    benefit_group?: string;
                    benefit_group_name?: string;
                    carrier?: string;
                    account?: string;
                    gender: string;
                    phone?: string;
                    secondary_phone?: string;
                    email?: string;
                    member_address1?: string;
                    member_address2?: string;
                    member_city?: string;
                    member_state?: string;
                    member_zip?: string;
                    enrollment_status?: string;
                    enrollment_start?: string;
                    enrollment_end?: string;
                    is_fmh_enrollment_end?: boolean;
                    pending_at?: string;
                    will_be_unmanaged_on?: string;
                    last_updated?: string;
                } | null,
            ) =>
                response
                    ? {
                          memberId: response.member_id,
                          firstName: response.firstname,
                          lastName: response.lastname,
                          gender: response.gender,
                          dateOfBirth: response.dateofbirth,
                          benefitGroup: response.benefit_group,
                          benefitGroupName: response.benefit_group_name,
                          carrier: response.carrier,
                          account: response.account,
                          addressLine1: response.member_address1,
                          addressLine2: response.member_address2,
                          city: response.member_city,
                          state: response.member_state,
                          zip: response.member_zip,
                          phone: response.phone,
                          secondaryPhone: response.secondary_phone,
                          email: response.email,
                          enrollmentStatus: response.enrollment_status,
                          enrollmentStart: response.enrollment_start,
                          enrollmentEnd: response.enrollment_end,
                          isFmhEnrollmentEnd: response.is_fmh_enrollment_end,
                          pendingAt: response.pending_at,
                          willBeUnmanagedOn: response.will_be_unmanaged_on,
                          lastUpdated: response.last_updated,
                      }
                    : undefined,
            transformErrorResponse: (err, _, { raiseNotFound = true }) => {
                if (err.status === 404) {
                    if (raiseNotFound) {
                        return handleError('Member not found.', err);
                    }
                    return err;
                }
                return handleError('An error occurred retrieving member details.', err);
            },
        }),
        getBenefitCheckConfig: build.query<number, { payerId: string; segmentId: string }>({
            query: ({ payerId, segmentId }) => ({
                url: `${payerId}/benefit_check_config/${segmentId}`,
                params: {
                    option_name: 'delayed_unmanagement_days',
                },
            }),
        }),
        postMemberDataUpdate: build.mutation<
            string,
            UpdateMemberDataPayload & {
                payerId?: string;
                raiseNotFound?: boolean;
            }
        >({
            query: ({ payerId, memberId, firstName, dateOfBirth, phone }) => ({
                url: `admin/${payerId}/members/payers/update`,
                method: 'POST',
                body: {
                    member_id: memberId,
                    firstname: firstName,
                    dateofbirth: dateOfBirth,
                    phone,
                },
            }),
            invalidatesTags: (_results, _error, { memberId }) => [
                { type: TagTypes.MEMBER_DETAILS, id: memberId },
            ],
            transformErrorResponse: handleRTKQueryError('An error occurred updating member data.'),
        }),
        getMemberAttributes: build.query<
            string[],
            { payerId: string; memberId: string; firstName: string; dateOfBirth: string }
        >({
            query: ({ payerId, memberId, firstName, dateOfBirth }) => ({
                url: `admin/${payerId}/members/attributes/lookup`,
                method: 'POST',
                body: {
                    member_id: memberId,
                    firstname: firstName,
                    dateofbirth: dateOfBirth,
                },
            }),
            transformResponse: (response: string[] | undefined) => response ?? [],
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving member attributes.',
            ),
        }),
    }),
});

export const {
    useGetBenefitCheckConfigQuery,
    useGetMemberDetailsQuery,
    useGetMemberAttributesQuery,
    usePostMemberDataUpdateMutation,
} = scopeAssessorApi;
